import React from 'react'
import { IconAlertTriangle } from '@tabler/icons-react'

/**
 * Shared confirm/action modal used across SWMS management pages.
 *
 * Props:
 *   confirm  – null (hidden) or { type, title, message, record?, label?, okLabel?, warningText? }
 *   onConfirm – called when the ok button is clicked
 *   onCancel  – called when the cancel button or backdrop is clicked
 *
 * type controls button colour:
 *   'delete' | 'retire' | 'archive'  → danger (red)
 *   'publish'                         → publish (blue)
 *   anything else                     → danger fallback
 */
export default function ConfirmModal({ confirm, onConfirm, onCancel }) {
    if (!confirm) return null

    const isDanger = ['delete', 'retire', 'archive'].includes(confirm.type)
    const okClass = isDanger ? 'danger' : 'publish'
    const iconClass = isDanger ? 'danger' : 'warn'

    const confirmLabel = confirm.okLabel
        ?? (confirm.type === 'delete' ? 'Delete'
            : confirm.type === 'retire' ? 'Retire'
            : confirm.type === 'archive' ? 'Archive'
            : confirm.type === 'publish' ? 'Publish'
            : 'Confirm')

    const warningText = confirm.warningText
        ?? (isDanger ? 'This action is irreversible.' : null)

    const itemLabel = confirm.label ?? (confirm.record && (confirm.record.title || confirm.record.name))

    return (
        <div className={`sm-confirm-overlay open`} onClick={onCancel}>
            <div className="sm-confirm-box" onClick={(e) => e.stopPropagation()}>
                <div className={`sm-confirm-icon ${iconClass}`}>
                    <IconAlertTriangle size={20} stroke={1.5} />
                </div>
                <p className="sm-confirm-title">{confirm.title}</p>
                {itemLabel && <span className="sm-confirm-item">{itemLabel}</span>}
                <p className="sm-confirm-body">{confirm.message}</p>
                {warningText && (
                    <div className="sm-confirm-warning">
                        <IconAlertTriangle size={14} stroke={1.5} style={{ marginTop: 2, flexShrink: 0 }} />
                        {warningText}
                    </div>
                )}
                <div className="sm-confirm-actions">
                    <button className="sm-confirm-cancel" onClick={onCancel}>Cancel</button>
                    <button className={`sm-confirm-ok ${okClass}`} onClick={onConfirm}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    )
}
